import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { motion } from "motion/react";
import {
  LandingNavbar,
  NAVBAR_HEIGHT,
} from "@/components/landing/LandingNavbar";
import { Footer } from "@/components/landing/Footer";
import { FloatingSubscribe } from "@/components/landing/PulseSubscribeBar";
import { samplePulses } from "@/data/landing/samplePulses";
import type { DailyPulse, PulseStory } from "@altair/shared/types";
import { resolveStorySlug } from "@/lib/news/slug";
import { readNewsHydrationData } from "@/lib/news/hydration";
import {
  PAYER_COLORS,
  DEFAULT_PAYER_COLOR,
  formatPulseDate,
} from "@/components/news/payer-colors";
import { ArrowRight } from "lucide-react";

interface InsightsListPageProps {
  /**
   * Pulses to render. When omitted, falls back to the bundled `samplePulses`
   * (used during local dev and pre-cutover SPA preview). In production, the
   * Fastify SSR renderer passes DB-backed data here; the CSR hydration picks
   * it up from `window.__NEWS_DATA__`.
   */
  pulses?: DailyPulse[];
}

/**
 * Read SSR-hydrated pulse data from the `#__news-data__` JSON element the
 * news-renderer injects (see lib/news/hydration). Dev/SPA preview has no such
 * element, so this returns null and the caller falls back to bundled samples.
 */
function readInitialPulses(): DailyPulse[] | null {
  return readNewsHydrationData()?.pulses ?? null;
}

/* ── Page ──────────────────────────────────────────────────── */

const PULSES_PER_PAGE = 3;

export default function InsightsListPage({
  pulses,
}: InsightsListPageProps = {}) {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(PULSES_PER_PAGE);
  const navbarHeight = NAVBAR_HEIGHT;

  // Resolution order:
  //   1. Explicit prop (used when this page is composed inside the SSR bundle)
  //   2. `window.__NEWS_DATA__.pulses` (production CSR hydration)
  //   3. Bundled `samplePulses` (local dev / SPA preview)
  // Widening `samplePulses` to `DailyPulse[]` is safe: the shared type adds
  // only optional fields (slug, ogImageUrl, description, updatedAt) that the
  // static data legitimately omits.
  const data: DailyPulse[] =
    pulses ?? readInitialPulses() ?? (samplePulses as DailyPulse[]);

  const visiblePulses = data.slice(0, visibleCount);
  const hasMore = visibleCount < data.length;

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-dvh">
      <LandingNavbar
        isProduct={false}
        onBack={() => {
          // Force a full-page nav so / loads from the fresh main-webapp
          // deploy. /news is served by a separate news-renderer SSR
          // service that references an older bundle, and SPA-navigating
          // back to / would reuse that stale JS to render the old
          // landing page (e.g. pre-launch countdown navbar).
          window.location.href = "/";
        }}
      />

      <main>
        {/* Header area */}
        <div
          className="px-5"
          style={{
            paddingTop: `max(${navbarHeight + 56}px, 14vh)`,
          }}
        >
          <div className="mx-auto" style={{ maxWidth: 900 }}>
            <header>
              <h1
                className="font-semibold tracking-tight text-foreground"
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "clamp(34px, 5vw, 52px)",
                  lineHeight: 1.1,
                }}
              >
                Billing News
              </h1>
              <p
                className="mt-3"
                style={{
                  fontSize: 15,
                  lineHeight: 1.65,
                  color: "hsl(var(--foreground) / 0.60)",
                  maxWidth: 680,
                }}
              >
                Daily intelligence on medical billing, payer policy, and
                healthcare regulation.
                <br />
                Sourced from verified industry reports.
              </p>
              <div
                className="mt-10"
                style={{
                  borderTop: "1px solid hsl(var(--foreground) / 0.08)",
                }}
              />
            </header>

            {/* Article Feed */}
            <div className="mt-10" style={{ paddingBottom: 64 }}>
              {visiblePulses.map((pulse, idx) => (
                <motion.div
                  key={pulse.date}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.45,
                    ease: [0.16, 1, 0.3, 1],
                    delay:
                      idx >= visibleCount - PULSES_PER_PAGE
                        ? (idx - (visibleCount - PULSES_PER_PAGE)) * 0.1
                        : 0,
                  }}
                >
                  <DateSection pulse={pulse} />
                </motion.div>
              ))}

              {/* Load more */}
              {hasMore && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={() =>
                      setVisibleCount((c) =>
                        Math.min(c + PULSES_PER_PAGE, data.length)
                      )
                    }
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "hsl(var(--foreground))",
                      background: "hsl(0 0% 100%)",
                      border: "1px solid hsl(var(--primary) / 0.18)",
                      borderRadius: 9999,
                      padding: "10px 28px",
                      cursor: "pointer",
                      transition:
                        "border-color 200ms, color 200ms, background 200ms",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "hsl(var(--foreground) / 0.08)";
                      e.currentTarget.style.color =
                        "hsl(var(--foreground) / 0.50)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor =
                        "hsl(var(--primary) / 0.18)";
                      e.currentTarget.style.color = "hsl(var(--foreground))";
                    }}
                  >
                    Show more
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom spacer for floating CTA */}
        <div style={{ height: 80 }} />
      </main>

      <Footer />

      {/* Floating subscribe pill */}
      <FloatingSubscribe />
    </div>
  );
}

/* ── Date Section ─────────────────────────────────────────── */

function DateSection({ pulse }: { pulse: DailyPulse }) {
  const [showAll, setShowAll] = useState(false);
  const visibleStories = showAll ? pulse.stories : pulse.stories.slice(0, 3);

  return (
    <div style={{ marginBottom: 40 }}>
      {/* Date header */}
      <div className="flex items-center justify-between mb-4">
        <div
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 17,
            fontWeight: 600,
            color: "hsl(var(--foreground))",
          }}
        >
          {formatPulseDate(pulse.date)}
        </div>
        <span style={{ fontSize: 12, color: "hsl(var(--foreground) / 0.35)" }}>
          {pulse.storyCount} stories
        </span>
      </div>

      {/* Stories */}
      <div className="flex flex-col gap-2.5">
        {visibleStories.map((story, i) => (
          <StoryRow key={i} story={story} date={pulse.date} index={i} />
        ))}
      </div>

      {/* Show all */}
      {!showAll && pulse.stories.length > 3 && (
        <button
          onClick={() => setShowAll(true)}
          className="flex items-center gap-1.5 mt-3"
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "hsl(var(--foreground) / 0.45)",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
            transition: "color 150ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "hsl(var(--foreground))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "hsl(var(--foreground) / 0.45)";
          }}
        >
          Show all {pulse.storyCount} stories
          <ArrowRight size={13} />
        </button>
      )}
    </div>
  );
}

/* ── Story Row ────────────────────────────────────────────── */

function StoryRow({
  story,
  date,
  index,
}: {
  story: PulseStory;
  date: string;
  index: number;
}) {
  const slug = resolveStorySlug(story);
  // Deep-link to /news/:date/:slug when we have a slug (SSR + post-seed data);
  // fall back to `#story-${index}` for legacy static entries without slugs.
  const href = slug ? `/news/${date}/${slug}` : `/news/${date}#story-${index}`;
  // reloadDocument → full page load. The news-renderer SSRs each /news route
  // with that route's own data; client-side routing would render the story from
  // the list payload (which has no single pulse) and 404 ("Issue not found").
  return (
    <Link
      to={href}
      reloadDocument
      className="block no-underline"
      style={{ color: "inherit" }}
    >
      <div
        style={{
          background: "hsl(0 0% 100%)",
          border: "1px solid hsl(var(--foreground) / 0.06)",
          borderRadius: 12,
          padding: "16px 20px",
          boxShadow: "0 1px 2px 0 hsl(222 47% 11% / 0.03)",
          transition: "border-color 200ms, box-shadow 200ms",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "hsl(var(--primary) / 0.15)";
          e.currentTarget.style.boxShadow =
            "0 4px 12px -2px hsl(222 47% 11% / 0.06)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "hsl(var(--foreground) / 0.06)";
          e.currentTarget.style.boxShadow =
            "0 1px 2px 0 hsl(222 47% 11% / 0.03)";
        }}
      >
        {/* Headline */}
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            color: "hsl(var(--foreground))",
            lineHeight: 1.45,
          }}
        >
          {story.headline}
        </div>

        {/* Source + payer tags */}
        <div
          className="flex items-center gap-2 flex-wrap mt-1.5"
          style={{ fontSize: 12 }}
        >
          <span style={{ color: "hsl(var(--foreground) / 0.40)" }}>
            {story.source} · {story.sourceDate}
          </span>
          {story.payers.length > 0 && (
            <div className="flex items-center gap-1">
              {story.payers.map((payer) => {
                const pc = PAYER_COLORS[payer] || DEFAULT_PAYER_COLOR;
                return (
                  <span
                    key={payer}
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: pc.color,
                      background: pc.bg,
                      padding: "2px 7px",
                      borderRadius: 4,
                    }}
                  >
                    {payer}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
